import axios from "axios";
import { API_BASE_URL, ApiError } from "./api";
import { authService } from "./auth";

// Notification data types
export interface NotificationData {
  id: string;
  targetWalletAddress: string;
  title: string;
  content: string;
  type: 'info' | 'warning' | 'success' | 'announcement';
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  expiresAt?: string;
}

export interface CreateNotificationRequest {
  targetWalletAddress: string;
  title: string;
  content: string;
  type: 'info' | 'warning' | 'success' | 'announcement';
  expiresAt?: string;
}

// Helper function to extract error details from axios error
function handleApiError(error: any): never {
  if (error.response?.data) {
    // Backend returned structured error response
    const { error: message, code, timestamp } = error.response.data;
    const statusCode = error.response.status;

    throw new ApiError(
      message || "An error occurred",
      statusCode,
      code || "API_ERROR",
      timestamp,
    );
  }

  // Network or other error
  if (error.code === "ECONNABORTED") {
    throw new ApiError("Request timeout", 408, "TIMEOUT_ERROR");
  }

  if (error.message) {
    throw new ApiError(error.message, 500, "NETWORK_ERROR");
  }

  throw new ApiError("An unknown error occurred", 500, "UNKNOWN_ERROR");
}

export class NotificationApiService {
  private static instance: NotificationApiService;

  public static getInstance(): NotificationApiService {
    if (!NotificationApiService.instance) {
      NotificationApiService.instance = new NotificationApiService();
    }
    return NotificationApiService.instance;
  }

  /**
   * Get unread notifications for the authenticated user
   */
  async getUnreadNotifications(): Promise<NotificationData[]> {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new ApiError("Authentication required", 401, "AUTH_REQUIRED");
      }

      const response = await axios.get(`${API_BASE_URL}/notifications/unread`, {
        headers: {
          Authorization: token,
        },
      });

      return response.data.data;
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Mark a notification as read
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new ApiError("Authentication required", 401, "AUTH_REQUIRED");
      }

      await axios.post(
        `${API_BASE_URL}/notifications/${notificationId}/mark-read`,
        {},
        {
          headers: {
            Authorization: token,
          },
        }
      );
    } catch (error) {
      handleApiError(error);
    }
  }

  // Admin methods
  /**
   * Create a new notification (admin only)
   */
  async createNotification(notificationData: CreateNotificationRequest): Promise<string> {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new ApiError("Authentication required", 401, "AUTH_REQUIRED");
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/notifications`,
        notificationData,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      return response.data.data.id;
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Get all notifications with optional filters (admin only)
   */
  async getAllNotifications(filters?: {
    walletAddress?: string;
    type?: NotificationData['type'];
    isRead?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ notifications: NotificationData[]; total: number; limit: number; offset: number }> {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new ApiError("Authentication required", 401, "AUTH_REQUIRED");
      }

      const params = new URLSearchParams();
      if (filters?.walletAddress) params.append('walletAddress', filters.walletAddress);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.isRead !== undefined) params.append('isRead', filters.isRead.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await axios.get(
        `${API_BASE_URL}/admin/notifications?${params.toString()}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      return response.data.data;
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Get a specific notification by ID (admin only)
   */
  async getNotificationById(notificationId: string): Promise<NotificationData> {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new ApiError("Authentication required", 401, "AUTH_REQUIRED");
      }

      const response = await axios.get(
        `${API_BASE_URL}/admin/notifications/${notificationId}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );

      return response.data.data;
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Delete a notification (admin only)
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new ApiError("Authentication required", 401, "AUTH_REQUIRED");
      }

      await axios.delete(`${API_BASE_URL}/admin/notifications/${notificationId}`, {
        headers: {
          Authorization: token,
        },
      });
    } catch (error) {
      handleApiError(error);
    }
  }
}

// Export singleton instance
export const notificationApi = NotificationApiService.getInstance();